import React, { useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import type { Dispatch } from '@tmapy/redux';
import { useMessage, useSelectMessages } from '@tmapy/intl';

import {
  BtnBar,
  Form,
  FormItem,
  Input,
  CtaBtn,
  SecondaryBtn,
  StickyFooter,
  FormRow,
  Select,
} from '@tmapy/style-guide';

import { isVyjadreniPrijatoMessages, svuBtnMessages, svuMessages } from './svuMessages';
import { VYJADRENI_PRIJATO } from '../constants';
import { useInput } from '../../hooks';
import { InputProps } from '../../hooks/useInput';
import { setSvuListFilter } from '../../redux/filter/actions';

import { R7UrceniEditoraZpsListQuery } from '../../openapi/operations/r7UrceniEditoraZpsList';

type SvuFiltersProps = {
  filters: R7UrceniEditoraZpsListQuery;
  dispatch: Dispatch;
};

export const SvuFilter: React.FC<SvuFiltersProps> = ({ filters, dispatch }) => {
  const formatMessage = useMessage();

  // NAME
  const nazev = useInput<InputProps>({
    name: 'nazev__icontains',
    initialValue: filters.nazev__icontains,
    label: formatMessage(svuMessages.name),
  });

  // ID UZEMNI EDITACE
  const id_uzemni_editace = useInput<InputProps>({
    name: 'id_uzemni_editace__icontains',
    initialValue: filters.id_uzemni_editace__icontains,
    label: formatMessage(svuMessages.idUzemniEditace),
  });

  // CREATED (from)
  const [createdGte, setCreateGte] = useState(filters.created__gte);
  const created__gte = {
    name: 'created__gte',
    clear: () => {
      setCreateGte('');
    },
  };

  const handleCreateGteChange = (
    e: React.ChangeEvent,
    eventData: { name: string; value: string },
  ) => {
    setCreateGte(eventData.value);
  };

  // CREATED (to)
  const [createdLte, setCreateLte] = useState(filters.created__lte);
  const created__lte = {
    name: 'created__lte',
    clear: () => {
      setCreateLte('');
    },
  };

  const handleCreateLteChange = (
    e: React.ChangeEvent,
    eventData: { name: string; value: string },
  ) => {
    setCreateLte(eventData.value);
  };

  // EDITED (from)
  const [editedGte, setEditedGte] = useState(filters.edited__gte);
  const edited__gte = {
    name: 'edited__gte',
    clear: () => {
      setEditedGte('');
    },
  };

  const handleEditedGteChange = (
    e: React.ChangeEvent,
    eventData: { name: string; value: string },
  ) => {
    setEditedGte(eventData.value);
  };

  // EDITED (to)
  const [editedLte, setEditedLte] = useState(filters.edited__lte);
  const edited__lte = {
    name: 'edited__lte',
    clear: () => {
      setEditedLte('');
    },
  };

  const handleEditedLteChange = (
    e: React.ChangeEvent,
    eventData: { name: string; value: string },
  ) => {
    setEditedLte(eventData.value);
  };

  // VYJADŘENÍ PŘIJATO
  const vyjadreni_prijato = useInput({
    name: 'vyjadreni_prijato',
    initialValue: filters.vyjadreni_prijato as any,
    items: useSelectMessages(isVyjadreniPrijatoMessages, VYJADRENI_PRIJATO),
    label: formatMessage(svuMessages.vyjadreniPrijato),
  });

  // EDITOR
  const editor_full_name = useInput<InputProps>({
    name: 'editor_full_name',
    initialValue: filters.editor_full_name,
    label: formatMessage(svuMessages.vyjadreniEditor),
  });

  const filterClearScope = [
    nazev,
    id_uzemni_editace,
    editor_full_name,
    created__gte,
    created__lte,
    edited__gte,
    edited__lte,
    vyjadreni_prijato,
  ];

  const handleClearBtnClick = useCallback(() => {
    filterClearScope.forEach((formItem) => formItem?.clear?.());

    dispatch(setSvuListFilter({}));
  }, [filterClearScope, dispatch]);

  const handleSubmit = () => {
    dispatch(
      setSvuListFilter({
        nazev__icontains: nazev.value,
        id_uzemni_editace__icontains: id_uzemni_editace.value,
        editor_full_name: editor_full_name.value,
        created__gte: createdGte,
        created__lte: createdLte,
        edited__gte: editedGte,
        edited__lte: editedLte,
        vyjadreni_prijato: vyjadreni_prijato.value,
      }),
    );
  };

  return (
    <Form onSubmit={handleSubmit}>
      <div className='sg-u-vs-2'>
        <FormItem {...nazev}>
          <Input {...nazev} />
        </FormItem>
        <FormItem {...id_uzemni_editace}>
          <Input {...id_uzemni_editace} />
        </FormItem>
        <FormItem {...editor_full_name}>
          <Input {...editor_full_name} />
        </FormItem>
        <FormRow>
          <FormItem label={formatMessage(svuMessages.createdGte)}>
            <Input
              type='date'
              name='created__gte'
              value={createdGte}
              onChange={handleCreateGteChange}
            />
          </FormItem>
          <FormItem label={formatMessage(svuMessages.createdLte)}>
            <Input
              type='date'
              name='created__lte'
              value={createdLte}
              onChange={handleCreateLteChange}
            />
          </FormItem>
        </FormRow>
        <FormRow>
          <FormItem label={formatMessage(svuMessages.editedGte)}>
            <Input
              type='date'
              name='edited__gte'
              value={editedGte}
              onChange={handleEditedGteChange}
            />
          </FormItem>
          <FormItem label={formatMessage(svuMessages.editedLte)}>
            <Input
              type='date'
              name='edited__lte'
              value={editedLte}
              onChange={handleEditedLteChange}
            />
          </FormItem>
        </FormRow>
        <FormRow>
          <FormItem {...vyjadreni_prijato}>
            <Select hasEmptyItem {...vyjadreni_prijato} />
          </FormItem>
        </FormRow>
      </div>
      <StickyFooter>
        <BtnBar stretch>
          <CtaBtn type='submit'>
            <FormattedMessage {...svuBtnMessages.filterSubmit} />
          </CtaBtn>
          <SecondaryBtn isDanger onClick={handleClearBtnClick}>
            <FormattedMessage {...svuBtnMessages.clear} />
          </SecondaryBtn>
        </BtnBar>
      </StickyFooter>
    </Form>
  );
};
